import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useSocket } from '../../contexts/SocketContext';
import Modal from '../../components/Modal';
import TabBar from '../../components/TabBar';
import { FileDown, ArrowLeft, FileSpreadsheet } from 'lucide-react';
import api from '../../utils/api';
import AdminNotificationBell from '../../components/AdminNotificationBell';

export default function ImportPage() {
  const navigate = useNavigate();
  const [textInput, setTextInput] = useState('');
  const [hasOldData, setHasOldData] = useState(false);
  const [oldDataCount, setOldDataCount] = useState(0);
  const [showModeModal, setShowModeModal] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [sourceLabel, setSourceLabel] = useState('粘贴文本');
  const { showToast } = useToast();
  const { subscribe } = useSocket();

  // P3-16：xlsx 体积 ~700KB，缓存已加载的模块，避免每次选文件都重新 import 下载。
  // （注：本项目 utils/share.js 静态 import 了 xlsx，xlsx 本来就打进 main chunk、只下载一次；
  //  这里缓存仍是正确的微优化，避免每次 await import() 的 Promise 调度开销。）
  const xlsxRef = useRef(null);
  const ensureXLSX = async () => {
    if (!xlsxRef.current) xlsxRef.current = await import('xlsx');
    return xlsxRef.current;
  };

  const fetchPoolStatus = useCallback(() => {
    api.get('/data/pool/has-old-data').then(res => {
      setHasOldData(res.data.hasOldData);
      setOldDataCount(res.data.count);
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchPoolStatus(); }, [fetchPoolStatus]);

  // 数据池变化时实时刷新
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event === 'pool_updated' || event === 'stats_refresh') fetchPoolStatus();
    });
  }, [subscribe, fetchPoolStatus]);

  const parseInput = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    return lines.map(line => ({
      customer_name: '',
      customer_phone: line,
      extra_info: ''
    }));
  };

  // 解码文本文件：优先 UTF-8（自动去 BOM），失败则回退 GB18030（兼容 Windows 记事本保存的 ANSI/GBK txt）
  const decodeTextFile = async (file) => {
    const buf = await file.arrayBuffer();
    let text;
    try {
      text = new TextDecoder('utf-8', { fatal: true }).decode(buf);
    } catch (_) {
      try {
        text = new TextDecoder('gb18030').decode(buf);
      } catch (__) {
        text = new TextDecoder('utf-8').decode(buf);
      }
    }
    return text.replace(/^\uFEFF/, '');
  };

  // 从 txt 文本中提取手机号：每行取第一段（逗号/空格/制表符/分号分隔），非纯数字的行（表头、说明）跳过
  const parseTxtPhones = (text) => {
    const phones = [];
    for (const line of text.split(/\r\n|\n|\r/)) {
      const first = line.trim().split(/[\s,，;；、|]+/)[0] || '';
      const cell = first.replace(/["'“”‘’]/g, ''); // 去掉可能包裹的引号
      if (!/^\d+$/.test(cell)) continue; // 非纯数字行（表头/说明）跳过
      phones.push({ customer_name: '', customer_phone: cell, extra_info: '' });
    }
    return phones;
  };

  // 读取文件：支持 .txt（每行一个号码）和表格（.xlsx/.xls/.csv，取每行第一列），自动跳过表头行
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isTxt = /\.txt$/i.test(file.name) || file.type === 'text/plain';
    setFileLoading(true);
    try {
      let phones = [];
      if (isTxt) {
        const text = await decodeTextFile(file);
        phones = parseTxtPhones(text);
        if (phones.length === 0) { showToast('未在 txt 文件中读取到号码，请确认每行一个手机号', 'error'); return; }
      } else {
        // 动态加载 xlsx（体积较大，按需下载，避免拖慢首屏）；已加载则复用缓存
        const XLSX = await ensureXLSX();
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        for (const row of rows) {
          const raw = Array.isArray(row) ? (row[0] ?? '') : '';
          const cell = String(raw).trim().replace(/\s/g, '');
          // 跳过表头/说明行：第一个有效单元格不是数字则视为表头
          if (!/^\d+$/.test(cell)) continue;
          phones.push({ customer_name: '', customer_phone: cell, extra_info: '' });
        }
        if (phones.length === 0) { showToast('未在表格中读取到号码，请确认第一列为手机号', 'error'); return; }
      }
      setParsedData(phones);
      setFileName(file.name);
      setSourceLabel(isTxt ? 'txt 文件' : '表格文件');
    } catch (err) {
      showToast((isTxt ? 'txt 解析失败：' : '表格解析失败：') + (err.message || '请确认是 txt/xlsx/xls/csv 文件'), 'error');
    } finally {
      setFileLoading(false);
      // 允许重复选择同一文件
      e.target.value = '';
    }
  };

  const handleImport = async (mode) => {
    setShowModeModal(false);
    const data = parsedData.length > 0 ? parsedData : parseInput(textInput);
    if (data.length === 0) { showToast('请输入有效数据', 'error'); return; }
    setLoading(true);
    try {
      const res = await api.post('/data/import', { data, mode });
      showToast(res.data.message, 'success');
      setTextInput('');
      setParsedData([]);
      setFileName('');
      setSourceLabel('粘贴文本');
      fetchPoolStatus(); // 立即刷新，socket 断线时也能更新
    } catch (err) {
      showToast(err.response?.data?.error || '导入失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    const data = parsedData.length > 0 ? parsedData : parseInput(textInput);
    if (data.length === 0) { showToast('请输入数据或选择文件', 'error'); return; }
    setParsedData(data);
    if (hasOldData) {
      setShowModeModal(true);
    } else {
      handleImport('append');
    }
  };

  const lineCount = textInput.split('\n').filter(l => l.trim()).length;

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}
        >
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileDown size={16} /> 数据导入
        </span>
        </span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* 数据池状态 */}
        {oldDataCount > 0 && (
          <div className="card" style={{ background: 'rgba(255,149,0,0.06)', border: '1px solid rgba(255,149,0,0.2)', marginBottom: 12, padding: 12 }}>
            <p style={{ fontSize: 14, color: '#9a6400', fontWeight: 600 }}>
              当前数据池剩余 {oldDataCount} 条可用数据
            </p>
          </div>
        )}

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>输入数据</h3>
          <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 12 }}>
            每行一个手机号，直接粘贴，或上传文件（txt 每行一个号码；xlsx/xls/csv 取每行第一列号码）
          </p>

          <textarea
            className="input"
            rows={10}
            placeholder={'13800138000\n13900139000\n13700137000'}
            value={textInput}
            onChange={e => setTextInput(e.target.value)}
            style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }}
          />

          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <label className="btn btn-outline" style={{ flex: 1, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer' }}>
              <FileSpreadsheet size={16} /> {fileName || '上传文件（txt/表格）'}
              <input type="file" accept=".txt,.xlsx,.xls,.csv,text/plain" style={{ display: 'none' }}
                onChange={handleFileSelect} />
            </label>
          </div>
          {fileLoading && <p style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 6 }}>解析文件中...</p>}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: 13, color: 'var(--c-text-secondary)' }}>
              {parsedData.length > 0 ? `${sourceLabel}已读取 ${parsedData.length} 条` : `${lineCount} 行数据`}
            </span>
            <button className="btn btn-primary btn-sm" onClick={handlePreview}
              disabled={(!textInput.trim() && parsedData.length === 0) || loading || fileLoading}>
              {loading ? '导入中...' : '导入数据'}
            </button>
          </div>
        </div>

        {/* Preview */}
        {parsedData.length > 0 && (
          <div className="card">
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>预览 ({parsedData.length} 条)</h3>
            {parsedData.slice(0, 5).map((item, i) => (
              <div key={i} style={{ padding: '6px 0', borderBottom: '0.5px solid var(--c-border)', fontSize: 14, color: 'var(--c-text)' }}>
                {item.customer_phone}
              </div>
            ))}
            {parsedData.length > 5 && (
              <p style={{ fontSize: 12, color: 'var(--c-text-secondary)', textAlign: 'center', padding: 8 }}>
                ... 等 {parsedData.length} 条数据
              </p>
            )}
          </div>
        )}

      {/* Mode selection modal */}
      <Modal show={showModeModal} onClose={() => setShowModeModal(false)} title="导入模式">
        <p style={{ fontSize: 14, color: 'var(--c-text-secondary)', marginBottom: 16 }}>
          当前池中还有 {oldDataCount} 条剩余数据，请选择导入模式：
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button className="btn btn-primary" onClick={() => handleImport('append')}>
            追加导入（保留旧数据）
          </button>
          <button className="btn btn-danger" onClick={() => handleImport('replace')}>
            替换导入（清空旧数据）
          </button>
          <button className="btn btn-outline" onClick={() => setShowModeModal(false)}>
            取消
          </button>
        </div>
      </Modal>
      </div>

      <TabBar mode="admin" />
    </div>
  );
}

